import { Inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { Meta, Title } from "@angular/platform-browser";
import { Subject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class SeoService {
  private renderer: Renderer2;
  jsonSchema$ = new Subject();
  constructor(
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    private title: Title,
    private meta: Meta
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  setPageTitle(title: string) {
    this.title.setTitle(title);

    const isOGTitleTagExists = !!this.meta.getTag('name="og:title"');

    this.meta[isOGTitleTagExists ? "updateTag" : "addTag"]({
      name: "og:title",
      content: title,
    });
  }

  setPageDescription(description: string) {
    const isDescTagExists = !!this.meta.getTag('name="description"');
    const isOGDescTagExists = !!this.meta.getTag('name="og:description"');

    this.meta[isDescTagExists ? "updateTag" : "addTag"]({
      name: "description",
      content: description,
    });

    this.meta[isOGDescTagExists ? "updateTag" : "addTag"]({
      name: "og:description",
      content: description,
    });
  }

  setCanonicalUrl(url: string) {
    this.removeAllCanonicalLinks();

    const canonicalLinkElm: HTMLLinkElement =
      this.renderer.createElement("link");
    this.renderer.setAttribute(canonicalLinkElm, "rel", "canonical");
    this.renderer.setAttribute(canonicalLinkElm, "href", url);
    this.renderer.appendChild(this.document.head, canonicalLinkElm);
  }

  removeAllCanonicalLinks() {
    this.document
      .querySelectorAll('link[rel="canonical"]')
      ?.forEach((link) => link.remove());
  }

  appendOrUpdateJsonLd(jsonLdData: { [key: string]: any }) {
    const existingScript = this.document.querySelector(
      'script[type="application/ld+json"]'
    );

    if (existingScript) {
      // Parse existing JSON-LD data
      try {
        const existingData = JSON.parse(existingScript.textContent || "");

        // Merge existing data with new data (shallow merge)
        const mergedData = { ...existingData, ...jsonLdData };

        // Update the script content
        existingScript.textContent = JSON.stringify(mergedData, null, 2);
      } catch (error) {
        console.error("Error parsing existing JSON-LD:", error);
      }
    } else {
      // If no existing script, create a new one
      const script = this.document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(jsonLdData, null, 2);
      this.document.head.appendChild(script);
    }
  }

  removeAllJsonLds() {
    this.document
      .querySelectorAll('script[type="application/ld+json"]')
      ?.forEach((link) => link.remove());
  }
}
